import { SpotMatchRecord, Role } from 'api/v1/spot'
import { orderModalAtom } from 'components/MobileUserOrderModalV3'
import { TrendColorizer } from 'components/TrendColorizer'
import { useSetAtom } from 'jotai'
import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSpotCurrencies } from 'store/market/hooks'
import { useSpotSymbols } from 'store/spot/hooks'
import { OrderDirection } from 'store/spot/types'
import { encryptionAddress } from 'utils/address'
import { DateFormatter } from 'utils/datetime'
import { getEffectiveDecimals, toFixed } from 'utils/number'
import { findSymbolByName } from 'utils/symbols'
import { useOrderValue } from 'hooks/useSizeCalculations'

import {
  MobileItemTitle,
  MobileItemData,
  MobileItemFlex,
  MobileColumnItem,
  MobileCardWrap,
} from '../styles'

const MobileRowItem = memo<{
  row: SpotMatchRecord
}>(({ row }) => {
  const { t } = useTranslation()
  const {
    symbol: symbolName = '',
    direction = '',
    matchQuantity = 0,
    matchPrice = 0,
    fee = 0,
    feeCurrency = '',
    txHash = '',
    createdAt = 0,
    type,
  } = row ?? {}
  const spotSymbols = useSpotSymbols()
  const tokenList = useSpotCurrencies()
  const symbol = findSymbolByName(spotSymbols, symbolName)
  const {
    baseCurrency = '',
    quoteCurrency = '',
    baseScale = 0,
    quoteScale = 0,
  } = symbol ?? {}

  const setSelectedOrder = useSetAtom(orderModalAtom)

  const token = tokenList.find((t) => baseCurrency === t.name)
  const { tokenIconUrl = '' } = token ?? {}

  const value = useOrderValue(String(matchQuantity), matchPrice)

  const effectiveFee = getEffectiveDecimals(fee)

  const fixedQuantity = toFixed(matchQuantity, baseScale)
  const fixedPrice = toFixed(matchPrice, quoteScale)
  const fixedValue = toFixed(value, quoteScale)

  const datetime = DateFormatter.toISODateTime(createdAt)
  return (
    <MobileCardWrap>
      <MobileItemFlex>
        <MobileColumnItem flex="1.3">
          <MobileItemTitle>Date</MobileItemTitle>
          <MobileItemData>{datetime}</MobileItemData>
        </MobileColumnItem>
        <MobileColumnItem>
          <MobileItemTitle>Pair</MobileItemTitle>
          <MobileItemData>
            {baseCurrency}/{quoteCurrency}
          </MobileItemData>
        </MobileColumnItem>
        <MobileColumnItem className="last">
          <MobileItemTitle>Side</MobileItemTitle>
          <MobileItemData>
            <TrendColorizer
              trend={direction === OrderDirection.LONG}
              value={
                direction === OrderDirection.LONG
                  ? t('user-order-table-content-buy')
                  : t('user-order-table-content-sell')
              }
            />
          </MobileItemData>
        </MobileColumnItem>
      </MobileItemFlex>
      <MobileItemFlex>
        <MobileColumnItem flex="1.3">
          <MobileItemTitle>Size</MobileItemTitle>
          <MobileItemData>
            {fixedQuantity} {baseCurrency}
          </MobileItemData>
        </MobileColumnItem>
        <MobileColumnItem>
          <MobileItemTitle>Price</MobileItemTitle>
          <MobileItemData>
            {fixedPrice} {quoteCurrency}
          </MobileItemData>
        </MobileColumnItem>
        <MobileColumnItem className="last">
          <MobileItemTitle>Fee</MobileItemTitle>
          <MobileItemData>
            {effectiveFee} {feeCurrency}
          </MobileItemData>
        </MobileColumnItem>
      </MobileItemFlex>
      <MobileItemFlex>
        <MobileColumnItem flex="1.3">
          <MobileItemTitle>Role</MobileItemTitle>
          <MobileItemData>
            {type === Role.MAKER ? 'Maker' : 'Taker'}
          </MobileItemData>
        </MobileColumnItem>
        <MobileColumnItem>
          <MobileItemTitle>Total</MobileItemTitle>
          <MobileItemData>{`${t(fixedValue)} ${quoteCurrency}`}</MobileItemData>
        </MobileColumnItem>
        <MobileColumnItem className="last">
          <MobileItemTitle>Tx Hash</MobileItemTitle>
          <MobileItemData className="hash">
            {txHash ? encryptionAddress(txHash) : '-'}
          </MobileItemData>
        </MobileColumnItem>
      </MobileItemFlex>
    </MobileCardWrap>
  )
})

export default MobileRowItem
